/**
 * Every feature by code, with the firm's own name for it — so a screen that has only an
 * order's codes says a renamed label in the firm's words rather than in ours (#462).
 *
 * Read once per console session and cached, as the line names are: an administrator renaming
 * a label is not something a dispatcher's order card needs to notice within the second. The
 * labels page forgets it after a change, so the same console sees its own edit.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { vehicleChoicesApi, type VehicleFeature } from './api';
import { featureName } from './words';

let cached: Promise<VehicleFeature[]> | null = null;

/** The list, fetched the first time anybody asks and shared after. */
export function featureList(): Promise<VehicleFeature[]> {
  cached ??= vehicleChoicesApi
    .get()
    .then((choices) => choices.features)
    .catch(() => {
      // Every code is then said by its translation, as before; the next asker tries again.
      cached = null;
      return [];
    });

  return cached;
}

/** Forgets the list, so the next asker fetches it again. For the page that edits it. */
export function forgetFeatures(): void {
  cached = null;
}

/** A feature's name by its code: the firm's words for a renamed one, ours otherwise. */
export function useFeatureName(): (code: string) => string {
  const { t } = useTranslation();
  const [features, setFeatures] = useState<VehicleFeature[]>([]);

  useEffect(() => {
    let cancelled = false;

    void featureList().then((list) => {
      if (!cancelled) setFeatures(list);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return useCallback(
    (code: string) => {
      const feature = features.find((f) => f.code === code);

      return featureName(t, code, feature?.displayName, feature?.namedByFirm);
    },
    [features, t],
  );
}
